import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Loader2,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Ban,
  Eye,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Button } from '@/components/ui/Button';
import {
  usePatientFormsAssignments,
  useResendQuestionnaire,
  useCancelQuestionnaire,
  type PatientFormsAssignment,
  type FormsAssignmentStatus,
} from '@/hooks/useClinioForms';
import { SendFormsAssignmentModal } from './SendFormsAssignmentModal';
import { FormsResponseViewModal } from './FormsResponseViewModal';
import { formsResponseUrl, publicUrlFromAssignmentMetadata } from '@/lib/formsUrls';

interface FormsAssignmentTabProps {
  patientId: string;
  clinicId: string;
}

function StatusPill({ status }: { status: FormsAssignmentStatus }) {
  const { t } = useTranslation();

  const map: Record<FormsAssignmentStatus, { label: string; cls: string; icon: React.ReactNode }> = {
    sent: {
      label: t('forms.status.sent', 'Sendt'),
      cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
      icon: <Clock className="mr-1 h-3 w-3" />,
    },
    reminded: {
      label: t('forms.status.reminded', 'Påminder'),
      cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
      icon: <RefreshCw className="mr-1 h-3 w-3" />,
    },
    completed: {
      label: t('forms.status.completed', 'Udfyldt'),
      cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
      icon: <CheckCircle className="mr-1 h-3 w-3" />,
    },
    expired: {
      label: t('forms.status.expired', 'Udløbet'),
      cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      icon: <XCircle className="mr-1 h-3 w-3" />,
    },
    cancelled: {
      label: t('forms.status.cancelled', 'Annulleret'),
      cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      icon: <Ban className="mr-1 h-3 w-3" />,
    },
  };

  const { label, cls, icon } = map[status] ?? map.sent;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {icon}
      {label}
    </span>
  );
}

function AssignmentDetailDrawer({
  assignment,
  onViewResponses,
  onClose,
}: {
  assignment: PatientFormsAssignment;
  onViewResponses: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const summary = assignment.template_summary;

  const fmtDate = (s: string | null) =>
    s ? format(new Date(s), 'dd. MMM yyyy HH:mm', { locale: da }) : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 sm:items-start sm:pt-8">
      <div className="w-full max-w-md overflow-y-auto rounded-t-xl bg-white shadow-xl dark:bg-gray-900 sm:rounded-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {summary?.name ?? assignment.forms_template_id}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3 p-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">{t('forms.detail.status', 'Status')}</span>
            <StatusPill status={assignment.status} />
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">{t('forms.detail.sent', 'Sendt')}</span>
            <span className="text-gray-900 dark:text-white">{fmtDate(assignment.sent_at)}</span>
          </div>
          {assignment.due_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('forms.detail.due', 'Frist')}</span>
              <span className="text-gray-900 dark:text-white">{fmtDate(assignment.due_at)}</span>
            </div>
          )}
          {assignment.completed_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('forms.detail.completed', 'Udfyldt')}</span>
              <span className="text-gray-900 dark:text-white">{fmtDate(assignment.completed_at)}</span>
            </div>
          )}
          {assignment.score && (
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
              <p className="mb-1 text-xs font-medium text-gray-500">{t('forms.detail.score', 'Score')}</p>
              <pre className="text-xs text-gray-700 dark:text-gray-300">
                {JSON.stringify(assignment.score, null, 2)}
              </pre>
            </div>
          )}
          {(assignment.status === 'sent' || assignment.status === 'reminded') && (
            <div className="flex flex-col gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
              {publicUrlFromAssignmentMetadata(assignment.metadata) && (
                <>
                  <a
                    href={publicUrlFromAssignmentMetadata(assignment.metadata)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t('forms.action_open_questionnaire', 'Åbn spørgeskema')}
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      const url = publicUrlFromAssignmentMetadata(assignment.metadata);
                      if (url) {
                        navigator.clipboard.writeText(url);
                      }
                    }}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  >
                    {t('forms.action_copy_link', 'Kopier link')}
                  </button>
                </>
              )}
            </div>
          )}
          {assignment.status === 'completed' && (
            <div className="flex flex-col gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
              <Button size="sm" className="w-full" onClick={onViewResponses}>
                <Eye className="mr-1.5 h-4 w-4" />
                {t('forms.action_view_responses', 'Se svar')}
              </Button>
              <a
                href={formsResponseUrl(assignment.forms_assignment_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {t('forms.responses.open_in_forms', 'Åbn i Clinio Forms')}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function FormsAssignmentTab({ patientId, clinicId }: FormsAssignmentTabProps) {
  const { t } = useTranslation();
  const { data: assignments, isLoading } = usePatientFormsAssignments(patientId);
  const resendMutation = useResendQuestionnaire();
  const cancelMutation = useCancelQuestionnaire();

  const [showSendModal, setShowSendModal] = useState(false);
  const [drawerAssignment, setDrawerAssignment] = useState<PatientFormsAssignment | null>(null);
  const [responseViewAssignment, setResponseViewAssignment] =
    useState<PatientFormsAssignment | null>(null);

  const fmtDate = (s: string | null) =>
    s ? format(new Date(s), 'dd. MMM yyyy', { locale: da }) : '—';

  const isTerminal = (status: FormsAssignmentStatus) =>
    ['completed', 'expired', 'cancelled'].includes(status);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-12 dark:border-gray-700 dark:bg-card-dark">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          {t('forms.section_title', 'Spørgeskemaer')}
        </h2>
        <Button size="sm" onClick={() => setShowSendModal(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t('forms.send_questionnaire', 'Send spørgeskema')}
        </Button>
      </div>

      {!assignments || assignments.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center dark:border-gray-700 dark:bg-card-dark">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            {t('forms.no_assignments_title', 'Ingen spørgeskemaer endnu')}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t('forms.no_assignments_desc', 'Send et spørgeskema til patienten for at komme i gang')}
          </p>
          <Button className="mt-6" onClick={() => setShowSendModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('forms.send_first', 'Send spørgeskema')}
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {[
                    t('forms.col_template', 'Skabelon'),
                    t('forms.col_sent', 'Sendt'),
                    t('forms.col_due', 'Frist'),
                    t('forms.col_status', 'Status'),
                    '',
                  ].map((h, i) => (
                    <th
                      key={i}
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                {assignments.map((a) => (
                  <tr
                    key={a.id}
                    className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    onClick={() => setDrawerAssignment(a)}
                  >
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {a.template_summary?.name ?? a.forms_template_id}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {fmtDate(a.sent_at)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {fmtDate(a.due_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <StatusPill status={a.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {a.status === 'completed' && (
                          <button
                            title={t('forms.action_view_responses', 'Se svar')}
                            onClick={() => setResponseViewAssignment(a)}
                            className="rounded p-1 text-gray-400 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-900/30 dark:hover:text-green-400"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        {!isTerminal(a.status) && (
                          <>
                            <button
                              title={t('forms.action_resend', 'Send påmindelse')}
                              disabled={resendMutation.isPending}
                              onClick={() => resendMutation.mutate({ assignment_id: a.id, patient_id: patientId })}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                            <button
                              title={t('forms.action_cancel', 'Annuller')}
                              disabled={cancelMutation.isPending}
                              onClick={() => cancelMutation.mutate({ assignment_id: a.id, patient_id: patientId })}
                              className="rounded p-1 text-gray-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 disabled:opacity-50"
                            >
                              <Ban className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showSendModal && (
        <SendFormsAssignmentModal
          patientId={patientId}
          clinicId={clinicId}
          onClose={() => setShowSendModal(false)}
        />
      )}

      {drawerAssignment && (
        <AssignmentDetailDrawer
          assignment={drawerAssignment}
          onViewResponses={() => {
            setResponseViewAssignment(drawerAssignment);
            setDrawerAssignment(null);
          }}
          onClose={() => setDrawerAssignment(null)}
        />
      )}

      {responseViewAssignment && (
        <FormsResponseViewModal
          clinicId={clinicId}
          formsAssignmentId={responseViewAssignment.forms_assignment_id}
          templateName={responseViewAssignment.template_summary?.name}
          onClose={() => setResponseViewAssignment(null)}
        />
      )}
    </section>
  );
}
